<template>
    <!-- Announcement is handled by the aria-live region this sits inside, so the label carries no role of its own. -->
    <div class="workiq-progress nadia-chat" data-testid="workiq-tool-progress">
        <CoachingModeMarble class="workiq-progress-marble" size="small" is-loading />
        <!--
            One label at a time, keyed by its text. The outgoing label is taken out of flow as it
            leaves so the incoming one crossfades over it — a sequential out-in swap left the row
            visibly empty between states.

            No ellipsis on any of them: the dots carry the waiting on Nadia's own steps, and a
            WorkIQ step reads as in-progress from its source chip, as in the demo.
        -->
        <TransitionGroup name="label-swap" tag="div" class="workiq-progress-labels">
            <p v-for="text in [label]" :key="text" class="workiq-progress-label">
                <span data-testid="workiq-progress-label">{{ text }}</span>
                <!-- Nadia's own steps carry the dots; a WorkIQ step carries the chip below instead. -->
                <ChatWorkIqThinkingDots v-if="!workIqSourced" class="workiq-progress-thinking-dots" />
                <!--
                    Attribution sits inside the label so it crossfades with it, and only for a step
                    WorkIQ actually ran — the memory lookup that opens the turn is Nadia's own.

                    BaseChip rather than SourceChip (which the Home WorkIQ cards use): SourceChip
                    hardcodes a dot in its leading slot, and this needs the org-chart glyph. Both
                    default to the same neutral chip tokens, so the chip itself looks identical.
                -->
                <BaseChip v-if="workIqSourced" size="xs" class="workiq-progress-source">
                    <template #leading>
                        <!-- Sized a step above the chip's own label, the proportion the demo's badge uses. -->
                        <i class="bi bi-diagram-2 text-[15px]/none" aria-hidden="true"></i>
                    </template>
                    {{ t("chat.thinking.workIqChip") }}
                </BaseChip>
            </p>
        </TransitionGroup>
    </div>
</template>

<script setup lang="ts">
import ChatWorkIqThinkingDots from "~vue/ChatWorkIqThinkingDots.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { useI18n } from "vue-i18n";

defineProps<{ label: string; workIqSourced?: boolean }>();

const { t } = useI18n();
</script>

<style scoped>
@reference "../../css/tailwind.css";

@layer components {
    .workiq-progress {
        @apply flex items-center gap-3;
    }

    .workiq-progress-marble {
        @apply shrink-0;
    }

    .workiq-progress-labels {
        @apply relative min-w-0 flex-1;
    }

    /* Type is inherited from the container's `nadia-chat`, matching the waiting message this replaces. */
    .workiq-progress-label {
        @apply text-ink-muted line-clamp-2;
    }

    .workiq-progress-thinking-dots {
        @apply ms-2.5;
    }

    /* Keeps the chip on the text's optical centre rather than its baseline. */
    .workiq-progress-source {
        @apply ms-2.5 align-middle;
    }

    .label-swap-enter-active,
    .label-swap-leave-active {
        @apply transition-opacity duration-300 ease-in-out;
    }

    .label-swap-enter-from,
    .label-swap-leave-to {
        @apply opacity-0;
    }

    .label-swap-leave-active {
        @apply absolute inset-x-0 top-0;
    }
}
</style>
